import { BuildingDefinition, BuildingId, Decimal } from '../types'

export const BUILDING_ORDER: readonly BuildingId[] = [
  'codingAssistant',
  'codingAgent',
  'laptopFarm',
  'modelWrapper',
  'pearPro',
  'gpu',
  'cloudCluster',
  'tpu',
  'serverRack',
  'customModel',
  'datacenter',
  'atomicEnergy',
  'hyperscaler',
  'quantumCompute',
  'dysonSphere',
] as const

export const buildings: ReadonlyMap<BuildingId, BuildingDefinition> = new Map([
  [
    'codingAssistant',
    {
      id: 'codingAssistant',
      name: 'Coding Assistant',
      baseCost: new Decimal(15),
      baseProduction: new Decimal(0.25),
      description: 'An AI helper that generates tokens passively.',
    },
  ],
  [
    'codingAgent',
    {
      id: 'codingAgent',
      name: 'Coding Agent',
      baseCost: new Decimal(100),
      baseProduction: new Decimal(1.25),
      description: 'An autonomous agent that works without supervision.',
    },
  ],
  [
    'laptopFarm',
    {
      id: 'laptopFarm',
      name: 'Used Laptop',
      baseCost: new Decimal(500),
      baseProduction: new Decimal(5),
      description: 'A collection of laptops running inference.',
    },
  ],
  [
    'modelWrapper',
    {
      id: 'modelWrapper',
      name: 'AI Wrapper',
      baseCost: new Decimal(1000),
      baseProduction: new Decimal(8),
      description: 'An API wrapper with a slick UI.',
    },
  ],
  [
    'pearPro',
    {
      id: 'pearPro',
      name: 'Pear Pro',
      baseCost: new Decimal(5000),
      baseProduction: new Decimal(20),
      description: 'Pear Silicon with unified memory.',
    },
  ],
  [
    'gpu',
    {
      id: 'gpu',
      name: 'GPU',
      baseCost: new Decimal(20000),
      baseProduction: new Decimal(120),
      description: 'A dedicated graphics card for AI inference.',
    },
  ],
  [
    'cloudCluster',
    {
      id: 'cloudCluster',
      name: 'Cloud Cluster',
      baseCost: new Decimal(160000),
      baseProduction: new Decimal(800),
      description: 'Reserved cloud GPU capacity.',
    },
  ],
  [
    'tpu',
    {
      id: 'tpu',
      name: 'TPU',
      baseCost: new Decimal(1280000),
      baseProduction: new Decimal(5000),
      description: 'Tensor Processing Units optimized for AI workloads.',
    },
  ],
  [
    'serverRack',
    {
      id: 'serverRack',
      name: 'Server Rack',
      baseCost: new Decimal(10240000),
      baseProduction: new Decimal(32000),
      description: 'A full rack in a colo facility.',
    },
  ],
  [
    'customModel',
    {
      id: 'customModel',
      name: 'Custom Model',
      baseCost: new Decimal(81920000),
      baseProduction: new Decimal(160000),
      description: 'A custom-trained AI model tailored to your needs.',
    },
  ],
  [
    'datacenter',
    {
      id: 'datacenter',
      name: 'Datacenter',
      baseCost: new Decimal(655360000),
      baseProduction: new Decimal(1000000),
      description: 'Your own datacenter.',
    },
  ],
  [
    'atomicEnergy',
    {
      id: 'atomicEnergy',
      name: 'Atomic Energy',
      baseCost: new Decimal(5242880000),
      baseProduction: new Decimal(6000000),
      description: 'Nuclear reactors powering your AI empire.',
    },
  ],
  [
    'hyperscaler',
    {
      id: 'hyperscaler',
      name: 'Hyperscaler',
      baseCost: new Decimal(41943040000),
      baseProduction: new Decimal(30000000),
      description: 'A global-scale cloud empire.',
    },
  ],
  [
    'quantumCompute',
    {
      id: 'quantumCompute',
      name: 'Quantum Compute',
      baseCost: new Decimal(335544320000),
      baseProduction: new Decimal(150000000),
      description: 'Quantum computers solving the impossible.',
    },
  ],
  [
    'dysonSphere',
    {
      id: 'dysonSphere',
      name: 'Dyson Sphere',
      baseCost: new Decimal(2684354560000),
      baseProduction: new Decimal(750000000),
      description: 'A megastructure harvesting stellar energy.',
    },
  ],
])

export const getBuilding = (id: BuildingId): BuildingDefinition | undefined =>
  buildings.get(id)
